// 📄 pages/Proyectos/components/VersionesSection.tsx
// "Versiones" (plan-proyectos-generales.md, G6): entregas del proyecto con
// los cambios aprobados y las tareas que incluyen. Planificada → En pruebas
// → Liberada (puede marcar sus cambios como implementados) o Cancelada.
// Notas de versión listas para copiar y enviar al cliente. El rol cliente
// lo ve en solo lectura.
import React, { useEffect, useMemo, useState } from 'react';
import { Rocket, Plus, RefreshCw, X, Save, Copy, Check, Pencil, Trash2, Calendar, GitPullRequest, ListTodo } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import { getCambios, Cambio } from '../../../services/cambioService.ts';
import { getTareas, Tarea } from '../../../services/tareaService.ts';
import {
  Version, VersionEstado, VersionFormData, AMBIENTES_VERSION, getVersiones, createVersion, updateVersion, cambiarEstadoVersion,
  liberarVersion, deleteVersion, notasDeVersion, cambiosIncluibles
} from '../../../services/versionService.ts';
import { getDisplayName } from '../../../utils/displayName.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

const ESTADO_STYLES: Record<VersionEstado, string> = {
  Planificada: 'text-gray-300 bg-white/5 border-white/15',
  'En pruebas': 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  Liberada: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  Cancelada: 'text-gray-500 bg-white/5 border-white/10'
};
const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

export default function VersionesSection() {
  const { project, projectId, esCliente } = useProject();
  const { user, profile } = useAuth();
  const [versiones, setVersiones] = useState<Version[]>([]);
  const [cambios, setCambios] = useState<Cambio[]>([]);
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Version | 'nueva' | null>(null);
  const [liberando, setLiberando] = useState<Version | null>(null);
  const [borrando, setBorrando] = useState<Version | null>(null);
  const [copiada, setCopiada] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const [v, c, t] = await Promise.all([getVersiones(projectId), getCambios(projectId).catch(() => [] as Cambio[]), getTareas(projectId).catch(() => [] as Tarea[])]);
      setVersiones(v);
      setCambios(c);
      setTareas(t);
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar las versiones.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (!project || !user) return null;
  const nombre = getDisplayName(user, profile);

  const ejecutar = async (accion: () => Promise<unknown>) => {
    setError(null);
    try {
      await accion();
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo completar la acción.');
    }
  };

  const copiarNotas = async (v: Version) => {
    await navigator.clipboard.writeText(notasDeVersion(v, project.nombre, tareas));
    setCopiada(v.id);
    setTimeout(() => setCopiada(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-400">Cada entrega del proyecto con lo que incluye. Las notas de versión se copian para enviarlas al cliente.</p>
        {!esCliente && (
          <button onClick={() => setForm('nueva')} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-white">
            <Plus className="w-4 h-4" /> Nueva versión
          </button>
        )}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando versiones...</p>
        </div>
      ) : versiones.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Rocket className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Todavía no hay versiones. Crea una cuando prepares una entrega (por ejemplo v1.0.0 a producción).</p>
        </div>
      ) : (
        <div className="space-y-3">
          {versiones.map((v) => (
            <div key={v.id} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="text-base font-semibold text-white">{v.numero}{v.titulo && <span className="text-gray-400 font-normal"> · {v.titulo.replace(/^[·\-–—|:]+\s*/, '')}</span>}</h4>
                  <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-1">
                    <Calendar className="w-3 h-3" />
                    {v.estado === 'Liberada' ? `Liberada el ${v.fechaLiberacion}` : v.fechaPlanificada ? `Planificada para el ${v.fechaPlanificada}` : 'Sin fecha'}
                    {v.ambiente && <span>· {v.ambiente}</span>}
                  </p>
                </div>
                <span className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${ESTADO_STYLES[v.estado]}`}>{v.estado}</span>
              </div>

              {v.notas && <p className="text-sm text-gray-300 whitespace-pre-wrap">{v.notas}</p>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-gray-400 flex items-center gap-1.5 mb-1"><GitPullRequest className="w-3.5 h-3.5" /> Cambios ({v.cambios.length})</p>
                  <ul className="space-y-0.5 text-gray-300">
                    {v.cambios.length ? v.cambios.map((c) => {
                      const actual = cambios.find((x) => x.id === c.id);
                      return <li key={c.id}>· {c.titulo}{actual && <span className="text-gray-500"> ({actual.estado})</span>}</li>;
                    }) : <li className="text-gray-600">Ninguno</li>}
                  </ul>
                </div>
                <div>
                  <p className="text-gray-400 flex items-center gap-1.5 mb-1"><ListTodo className="w-3.5 h-3.5" /> Tareas ({v.tareas.length})</p>
                  <ul className="space-y-0.5 text-gray-300">
                    {v.tareas.length ? v.tareas.map((t) => {
                      const actual = tareas.find((x) => x.id === t.id);
                      return <li key={t.id} className={actual?.estado === 'Completada' ? 'line-through text-gray-500' : ''}>· {t.titulo}</li>;
                    }) : <li className="text-gray-600">Ninguna</li>}
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 flex-wrap">
                <button onClick={() => copiarNotas(v)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-gray-300 bg-white/5 hover:bg-white/10">
                  {copiada === v.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copiar notas de versión
                </button>
                {!esCliente && (
                  <>
                    {v.estado === 'Planificada' && (
                      <button onClick={() => ejecutar(() => cambiarEstadoVersion(projectId, v.id, 'En pruebas', nombre))} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/15 text-white">Pasar a pruebas</button>
                    )}
                    {['Planificada', 'En pruebas'].includes(v.estado) && (
                      <button onClick={() => setLiberando(v)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-[#fff]"><Rocket className="w-3.5 h-3.5" /> Liberar</button>
                    )}
                    {['Planificada', 'En pruebas'].includes(v.estado) && (
                      <button onClick={() => ejecutar(() => cambiarEstadoVersion(projectId, v.id, 'Cancelada', nombre))} className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
                    )}
                    <button onClick={() => setForm(v)} title="Editar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                    {v.estado !== 'Liberada' && (
                      <button onClick={() => setBorrando(v)} title="Eliminar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {form && (
        <VersionFormModal
          version={form === 'nueva' ? null : form}
          cambios={cambiosIncluibles(cambios)}
          tareas={tareas}
          onClose={() => setForm(null)}
          onGuardar={(data) => ejecutar(async () => {
            if (form === 'nueva') await createVersion(projectId, data, user.uid, nombre);
            else await updateVersion(projectId, form.id, data, nombre);
            setForm(null);
          })}
        />
      )}

      {liberando && (
        <LiberarModal
          version={liberando}
          cambios={cambios}
          onClose={() => setLiberando(null)}
          onLiberar={(marcar) => ejecutar(async () => {
            await liberarVersion(projectId, liberando, cambios, marcar, user.uid, nombre);
            setLiberando(null);
          })}
        />
      )}

      {borrando && (
        <ConfirmDialog
          title="¿Eliminar esta versión?"
          message={`Se eliminará ${borrando.numero}. Sus cambios y tareas no se borran.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={() => ejecutar(async () => { await deleteVersion(projectId, borrando.id); setBorrando(null); })}
          onCancel={() => setBorrando(null)}
        />
      )}
    </div>
  );
}

function VersionFormModal({ version, cambios, tareas, onClose, onGuardar }: {
  version: Version | null; cambios: Cambio[]; tareas: Tarea[]; onClose: () => void; onGuardar: (data: VersionFormData) => Promise<void>;
}) {
  const [data, setData] = useState<VersionFormData>(version
    ? { numero: version.numero, titulo: version.titulo, notas: version.notas, ambiente: version.ambiente, fechaPlanificada: version.fechaPlanificada, cambios: version.cambios, tareas: version.tareas }
    : { numero: '', titulo: '', notas: '', ambiente: 'PRD', fechaPlanificada: '', cambios: [], tareas: [] });
  const [guardando, setGuardando] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(true);

  const tareasVisibles = useMemo(
    () => tareas.filter((t) => !soloPendientes || t.estado !== 'Completada' || data.tareas.some((x) => x.id === t.id)),
    [tareas, soloPendientes, data.tareas]
  );

  const alternar = (campo: 'cambios' | 'tareas', item: { id: string; titulo: string }) =>
    setData((d) => ({
      ...d,
      [campo]: d[campo].some((x) => x.id === item.id) ? d[campo].filter((x) => x.id !== item.id) : [...d[campo], { id: item.id, titulo: item.titulo }]
    }));

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    try { await onGuardar(data); } finally { setGuardando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{version ? `Editar ${version.numero}` : 'Nueva versión'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={guardar} className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Número*</label>
              <input autoFocus value={data.numero} onChange={(e) => setData({ ...data, numero: e.target.value })} maxLength={40} placeholder="v1.0.0" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Título</label>
              <input value={data.titulo} onChange={(e) => setData({ ...data, titulo: e.target.value })} maxLength={120} placeholder="ej. Pagos en caja" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Ambiente</label>
              <select value={data.ambiente} onChange={(e) => setData({ ...data, ambiente: e.target.value })} className={inputClass}>
                {AMBIENTES_VERSION.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Fecha planificada</label>
              <input type="date" value={data.fechaPlanificada} onChange={(e) => setData({ ...data, fechaPlanificada: e.target.value })} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Notas</label>
            <textarea value={data.notas} onChange={(e) => setData({ ...data, notas: e.target.value })} rows={3} maxLength={3000} className={`${inputClass} resize-none`} placeholder="Qué trae esta versión, instrucciones, consideraciones para el cliente..." />
          </div>

          <div>
            <p className={labelClass}>Cambios incluidos (aprobados)</p>
            {cambios.length === 0 ? <p className="text-xs text-gray-600">No hay solicitudes de cambio aprobadas.</p> : (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {cambios.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer hover:text-white">
                    <input type="checkbox" checked={data.cambios.some((x) => x.id === c.id)} onChange={() => alternar('cambios', c)} className="w-4 h-4 accent-blue-500" />
                    <span className="truncate">{c.titulo}</span><span className="text-[11px] text-gray-500">{c.estado}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <p className={labelClass}>Tareas incluidas</p>
              <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
                <input type="checkbox" checked={soloPendientes} onChange={(e) => setSoloPendientes(e.target.checked)} className="w-3 h-3 accent-blue-500" /> Ocultar completadas
              </label>
            </div>
            {tareasVisibles.length === 0 ? <p className="text-xs text-gray-600">No hay tareas para mostrar.</p> : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {tareasVisibles.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer hover:text-white">
                    <input type="checkbox" checked={data.tareas.some((x) => x.id === t.id)} onChange={() => alternar('tareas', t)} className="w-4 h-4 accent-blue-500" />
                    <span className="truncate">{t.titulo}</span>
                    {t.hitoNombre && <span className="text-[11px] text-blue-300/70">🏁 {t.hitoNombre}</span>}
                    <span className="text-[11px] text-gray-500">{t.estado}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
            <button type="submit" disabled={guardando} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white">
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Liberar: ofrece marcar como implementados los cambios incluidos que todavía no lo están. */
function LiberarModal({ version, cambios, onClose, onLiberar }: {
  version: Version; cambios: Cambio[]; onClose: () => void; onLiberar: (marcarCambios: boolean) => void;
}) {
  const pendientes = cambios.filter((c) => version.cambios.some((x) => x.id === c.id) && ['Aprobada', 'En implementación'].includes(c.estado));
  const [marcar, setMarcar] = useState(true);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2"><Rocket className="w-5 h-5 text-emerald-400" /> Liberar {version.numero}</h3>
        <p className="text-sm text-gray-400">Queda registrada como liberada hoy{version.ambiente ? ` en ${version.ambiente}` : ''}.</p>
        {pendientes.length > 0 && (
          <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={marcar} onChange={(e) => setMarcar(e.target.checked)} className="w-4 h-4 accent-emerald-500 mt-0.5" />
            <span>Marcar como implementados los {pendientes.length} cambio{pendientes.length > 1 ? 's' : ''} incluido{pendientes.length > 1 ? 's' : ''}: {pendientes.map((c) => c.titulo).join(', ')}.</span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
          <button onClick={() => onLiberar(marcar && pendientes.length > 0)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff]">
            <Rocket className="w-4 h-4" /> Liberar
          </button>
        </div>
      </div>
    </div>
  );
}
